import * as z from 'zod'

import {buildPageSchemaForTemplate} from '@github-ui/swp-core/schemas/contentful/contentTypes/containerPage'
import {buildEntrySchemaFor} from '@github-ui/swp-core/schemas/contentful/entry'
import {VendorPixelsSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/vendorPixel'
import {safeOptional} from '@github-ui/swp-core/schemas/safe-optional'

import {toEntryCollection} from '../../../lib/types/contentful/entryCollection'
import {findByIdWithSchema} from '../../lib/types/contentful'

const ContentEntrySchema = z.looseObject({
  sys: z.looseObject({
    id: z.string(),
    contentType: z.object({sys: z.object({id: z.string()})}),
  }),
  fields: z.looseObject({
    id: z.unknown().optional(),
  }),
})

const TemplateBrandShellSchema = buildEntrySchemaFor('templateBrand', {
  fields: z.lazy(() =>
    z.object({
      content: z.array(ContentEntrySchema),
      pixels: safeOptional(VendorPixelsSchema),
      faq: z.unknown().optional(),
      hero: z.unknown().optional(),
      subnav: z.unknown().optional(),
    }),
  ),
})

const BrandPageShellSchema = buildPageSchemaForTemplate(TemplateBrandShellSchema)

type BrandPageShell = z.infer<typeof BrandPageShellSchema>

type ParsedField<Content> = {
  value: Content
  errors: Error[]
}

export type BrandPageField<Content> = {
  parse: (page: BrandPageShell) => ParsedField<Content>
}

export type BrandPageDefinition = Record<string, BrandPageField<unknown>>

export type BrandPageContent<Definition extends BrandPageDefinition> = {
  [Key in keyof Definition]: Definition[Key] extends BrandPageField<infer Content> ? Content : never
}

/**
 * Keep definitions at module scope so their identity remains stable between renders.
 */
export function defineBrandPage<const Definition extends BrandPageDefinition>(definition: Definition): Definition {
  return definition
}

export function optionalSection<Schema extends z.ZodType, Content = z.output<Schema>>(
  id: string,
  schema: Schema,
  map: (sectionContent: z.output<Schema>) => Content = sectionContent => sectionContent as Content,
): BrandPageField<Content | undefined> {
  return {
    parse(page) {
      const sectionContent = findByIdWithSchema(page.fields.template.fields.content, id, schema)

      return {value: sectionContent ? map(sectionContent) : undefined, errors: []}
    },
  }
}

export function optionalTemplateField<Schema extends z.ZodType>(
  field: 'faq' | 'hero' | 'subnav',
  schema: Schema,
): BrandPageField<z.output<Schema> | undefined> {
  return {
    parse(page) {
      const value = page.fields.template.fields[field]

      return {value: value === undefined ? undefined : schema.parse(value), errors: []}
    },
  }
}

export function orderedSections<Schema extends z.ZodType, Content = z.output<Schema>>(
  schema: Schema,
  map: (sectionContent: z.output<Schema>) => Content = sectionContent => sectionContent as Content,
): BrandPageField<Content[]> {
  return {
    parse(page) {
      const value: Content[] = []
      const errors: Error[] = []

      for (const sectionContent of page.fields.template.fields.content) {
        try {
          value.push(map(schema.parse(sectionContent)))
        } catch (error) {
          errors.push(toError(error))
        }
      }

      return {value, errors}
    },
  }
}

export function parseBrandPage<Definition extends BrandPageDefinition>(
  contentfulPayload: unknown,
  definition: Definition,
): {colorMode: 'light' | 'dark'; content: BrandPageContent<Definition>; errors: Error[]} {
  const page = BrandPageShellSchema.parse(toEntryCollection(contentfulPayload).at(0))
  const content: Partial<Record<keyof Definition, unknown>> = {}
  const errors: Error[] = []

  for (const key of Object.keys(definition) as Array<keyof Definition>) {
    const field = definition[key]

    if (!field) continue

    try {
      const result = field.parse(page)
      content[key] = result.value
      errors.push(...result.errors)
    } catch (error) {
      content[key] = undefined
      errors.push(toError(error))
    }
  }

  return {
    colorMode: page.fields.settings?.fields.colorMode ?? 'dark',
    content: content as BrandPageContent<Definition>,
    errors,
  }
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}
